import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  Avatar,
  LinearProgress,
  IconButton,
  Chip,
  Tooltip,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Formik, Form } from "formik";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DoneAllIcon from "@mui/icons-material/DoneAll";

import { useLazyGetImageQuery } from "@/api/services/profile/profile_/FetchImageApi";
import { StyledButton } from "@/components";
import FormikTextInput from "@/components/forms/FormikTextInput";
import InputAdornment from "@mui/material/InputAdornment";
import {useAddAllocationMutation,useGetEmployeeDataQuery,
  useRmOverallSubmitMutation,useGetProjectDataQuery,useUpdateAllocationMutation} from "@/api/services/resourceAllocation/resourceAllocation";
import { toast } from "@/components/toastify";

// ─── Status configs ────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  pending: {
    label: "Pending",
    icon: <AccessTimeOutlinedIcon sx={{ fontSize: 14 }} />,
    chipSx: { 
      backgroundColor: "#FFF7ED",
      color: "#C2410C",
    },    
    cardBg: "#ffffff",
    border: "1px solid #E2E8F0",
    avatarRing: "#E2E8F0",
    headerBg: "#F8FAFC",
  },
  saved: {
    label: "Saved",
    icon: <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />,
    chipSx: { backgroundColor: "#EFF6FF", color: "#1D4ED8" },
    cardBg: "linear-gradient(145deg, #F8FAFC 0%, #F1F5F9 100%)",
    border: "1.5px solid #93C5FD",
    avatarRing: "#3B82F6",
    headerBg: "#EFF6FF",
  },
  submitted: {
    label: "Submitted",
    icon: <DoneAllIcon sx={{ fontSize: 14 }} />,
    chipSx: { backgroundColor: "#D1FAE5", color: "#065F46" },
    cardBg: "linear-gradient(145deg, #F0FDF8 0%, #ECFDF5 100%)",
    border: "1.5px solid #6EE7B7",
    avatarRing: "#10B981",
    headerBg: "#ECFDF5",
  },
};

// ─── Project Colors ───────────────────────────────────────────────────────────
const PROJECT_COLORS = [
  "#305533",  
  "#4A7A96",  
  "#475569",  
  "#0D9488",  
  "#64748B",  
  "#5D8A66", 
  "#3B82F6", 
];

// ─── Employee Card ─────────────────────────────────────────────────────────────
const EmployeeCard = ({ employee, profileUrl, status, onSave, onEdit, initialAllocations, isExisting, isLeaveInitial }) => {
  const [allocations, setAllocations] = useState(
    initialAllocations?.length > 0 ? initialAllocations : [{ project: "", percentage: 0 }]
  );
  const [isEditing, setIsEditing] = useState(status === "pending");
  const [isLeave, setIsLeave] = useState(isLeaveInitial || false);

  useEffect(() => {
    setIsLeave(isLeaveInitial || false);
  }, [isLeaveInitial]);

  // Sync allocations when initialAllocations loads from API
  useEffect(() => {
    if (initialAllocations?.length > 0) {
      setAllocations(initialAllocations);
    } else if (isLeaveInitial) {
      setAllocations([{ project: "", percentage: 0 }]);
    }
  }, [initialAllocations, isLeaveInitial]);

  useEffect(() => {
    if (status === "pending") setIsEditing(true);
    else setIsEditing(false);
  }, [status]);

  const totalPercentage = allocations.reduce((acc, curr) => acc + (Number(curr.percentage) || 0), 0);
  const isSaved = status === "saved";
  const isSubmitted = status === "submitted";
  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;

  const validAllocations = allocations.filter((a) => a.project && a.percentage > 0);

  const handleAddProject = () => {
    if (totalPercentage < 100) {
      setAllocations((prev) => [...prev, { project: "", percentage: 0 }]);
    }
  };

  const handleRemoveProject = (index) => {
    const updated = allocations.filter((_, i) => i !== index);
    const result = updated.length ? updated : [{ project: "", percentage: 0 }];
    setAllocations(result);
    recalcDuplicates(result);
  };

  const [duplicateIndexes, setDuplicateIndexes] = useState(new Set());
  const [fieldErrors, setFieldErrors] = useState({});

  // Recalculate all duplicate indexes for a given allocations array
  const recalcDuplicates = (allocs) => {
    const dupes = new Set();
    allocs.forEach((a, i) => {
      if (!a.project.trim()) return;
      const hasDup = allocs.some(
        (b, j) => j !== i && b.project.trim().toLowerCase() === a.project.trim().toLowerCase()
      );
      if (hasDup) dupes.add(i);
    });
    setDuplicateIndexes(dupes);
  };

  const handleChange = (index, field, value) => {
    const updated = [...allocations];
    if (field === "percentage") {
      let num = Number(value);
      if (num < 0) num = 0;
      const others = allocations.reduce((acc, c, i) => (i === index ? acc : acc + (Number(c.percentage) || 0)), 0);
      if (others + num > 100) num = 100 - others;
      updated[index][field] = num;
    } else {
      updated[index][field] = value;
      if (field === "project" && value.trim()) {
        const isDuplicate = updated.some(
          (a, i) => i !== index && a.project.trim().toLowerCase() === value.trim().toLowerCase()
        );
        if (isDuplicate) {
          toast("warning", "This project is already assigned to this employee");
        }
      }
      recalcDuplicates(updated);
    }
    setAllocations(updated);
  };

  const handleSaveClick = () => {
    if (!isLeave) {
      const errors = {};
      allocations.forEach((a, i) => {
        if (!a.project.trim()) errors[`p_${i}`] = "Project name is required";
        if (!a.percentage) errors[`pct_${i}`] = "Percentage is required";
      });
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return;
      }
    }
    setFieldErrors({});
    if (onSave) onSave(allocations, isLeave);
    setIsEditing(false);
  };

  const handleCancelClick = () => {
    if (initialAllocations?.length > 0) {
      setAllocations(initialAllocations);
    } else {
      setAllocations([{ project: "", percentage: 0 }]);
    }
    setIsLeave(isLeaveInitial || false);
    if (status !== "pending") setIsEditing(false);
  };

  return (
    <Card
      sx={{
            borderRadius: "16px",
            mt:1,
            border: statusCfg.border,
            background: statusCfg.cardBg,
            boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
            transition: "all 0.3s ease",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            opacity: isSubmitted ? 0.88 : 1,
            "&:hover": {
              transform: isSubmitted ? "none" : "translateY(-2px)",
              boxShadow: isSubmitted
                ? "0px 2px 8px rgba(0,0,0,0.05)"
                : "0px 6px 16px rgba(0,0,0,0.08)",
            },
          }}
          >
      {/* Header: Avatar, Name, EmpId, Status */}
      <Box
        sx={{
          px: 1.5,
          pt: 1.5,
          pb: 1,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          backgroundColor: statusCfg.headerBg,
          borderRadius: "16px 16px 0 0",
        }}
      >
        <Box
          sx={{
            p: "2px",
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${statusCfg.avatarRing}, ${statusCfg.avatarRing}66)`,
            flexShrink: 0,
          }}
        >
          <Avatar
            src={profileUrl || undefined}
            alt={employee?.employeeName || employee?.name}
            sx={{
              width: 40,
              height: 40,
              border: "2px solid #fff",
              filter: isSubmitted ? "grayscale(30%)" : "none",
              bgcolor: profileUrl ? "transparent" : "#bdbdbd;",
              fontSize: "16px",
              fontWeight: 700,
            }}
          >
            {!profileUrl && (employee?.employeeName || employee?.name || "E").charAt(0).toUpperCase()}
          </Avatar>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "13px",
              lineHeight: 1.3,
              color: isSubmitted ? "#6B7280" : "#1E293B",
            }}
            noWrap
          >
            {employee?.employeeName || employee?.name || "Employee"}
          </Typography>
          <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 500, fontSize: "11px" }}>
            {employee?.empId || "N/A"}
            {` · ${employee.department || employee.departmentName}`}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5, flexShrink: 0 }}>
          <Chip
            size="small"
            icon={statusCfg.icon}
            label={statusCfg.label}
            sx={{
              ...statusCfg.chipSx,
              fontWeight: 700,
              fontSize: "10px",
              height: 22,
            }}
          />
          {(isEditing || isLeave) && (
            <FormControlLabel
              control={
               <Checkbox
  size="small"
  checked={isLeave}
  onChange={(e) => setIsLeave(e.target.checked)}
  disabled={!isEditing}
  sx={{
    mt:1,
    p: 0.5,
    borderRadius: 1,
    color: "#b0b0b0",
    bgcolor: "#f5f5f5",
    transition: "all 0.2s ease",
    
    "& .MuiSvgIcon-root": {
      fontSize: 22,
    },

    "&.Mui-checked": {
      color: "#2e7d32",
      bgcolor: "#e8f5e9",
    },

    "&:hover": {
      bgcolor: "#eeeeee",
    },

    "&.Mui-checked:hover": {
      bgcolor: "#d7f0db",
    },

    "&.Mui-disabled": {
      opacity: 0.6,
    },
  }}
/>
              }
              label={<Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#64748B", ml: 0.5 ,mt:1}}>Leave</Typography>}
              sx={{ m: 0, '& .MuiFormControlLabel-label': { ml: 0.5 } }}
            />
          )}
        </Box>
      </Box>

      {/* Body: View vs Edit Mode */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", p: 1.5 }}>
        {isEditing ? (
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            {isLeave ? (
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 4, gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#64748B" }}>
                  This employee is on long leave.
                </Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#334155" }}>
                    Assign Projects
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: totalPercentage === 100 ? "#16A34A" : "#3B82F6",
                    }}
                  >
                    {totalPercentage}% / 100%
                  </Typography>
                </Box>

                <Box
                  sx={{
                flex: 1,
                maxHeight: "180px",
                overflowY: "auto",
                pr: 0.5,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                "&::-webkit-scrollbar": { width: "6px" },
                "&::-webkit-scrollbar-thumb": { backgroundColor: "#CBD5E1", borderRadius: "3px" },
              }}
            >
              <Formik
                initialValues={allocations.reduce((acc, a, i) => {
                  acc[`p_${i}`] = a.project;
                  acc[`pct_${i}`] = a.percentage === 0 ? "" : String(a.percentage);
                  return acc;
                }, {})}
                enableReinitialize
                onSubmit={() => {}}
              >
                {() => (
                  <Form>
                    {allocations.map((alloc, idx) => {
                      const color = PROJECT_COLORS[idx % PROJECT_COLORS.length];
                      return (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          p: 1,
                          mb: 1,
                          border: "1px solid",
                          borderColor: alloc.project ? `${color}40` : "#E2E8F0",
                          borderRadius: "10px",
                          backgroundColor: alloc.project ? `${color}08` : "#F8FAFC",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            borderColor: alloc.project ? `${color}80` : "#CBD5E1",
                            boxShadow: `0 2px 8px ${alloc.project ? `${color}1A` : "rgba(0,0,0,0.05)"}`
                          }
                        }}
                      >
                        <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: alloc.project ? color : "#CBD5E1", flexShrink: 0 }} />
                        <Tooltip title={alloc.project || ""} arrow disableHoverListener={!alloc.project || alloc.project.length < 20}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <TextField
                              value={alloc.project}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value.includes(",")) return;
                                handleChange(idx, "project", value);
                                setFieldErrors((prev) => { const n = {...prev}; delete n[`p_${idx}`]; return n; });
                              }}  
                           placeholder="Project name"
                              size="small"
                              fullWidth
                              error={duplicateIndexes.has(idx) || !!fieldErrors[`p_${idx}`]}
                              helperText={duplicateIndexes.has(idx) ? "Project already exists" : fieldErrors[`p_${idx}`] || ""}
                              sx={{
                                "& .MuiInputBase-input": { fontSize: "13px", fontWeight: 500, py: "6px", px: "10px", textOverflow: "ellipsis", color: "#1E293B" },
                                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                                "& .MuiInputBase-root": { backgroundColor: "#fff", borderRadius: "6px", border: duplicateIndexes.has(idx) ? "1px solid #EF4444" : "1px solid #E2E8F0" },
                                "&:hover .MuiInputBase-root": { borderColor: duplicateIndexes.has(idx) ? "#EF4444" : "#94A3B8" },
                                "& .Mui-focused": { borderColor: duplicateIndexes.has(idx) ? "#EF4444" : color, boxShadow: duplicateIndexes.has(idx) ? "0 0 0 2px #EF444433" : `0 0 0 2px ${color}33` },
                                "& .MuiFormHelperText-root": { fontSize: "10px", mt: 0.5, color: "#EF4444" },
                              }}
                            />
                          </Box>
                        </Tooltip>
                        <Box sx={{ width: "85px", flexShrink: 0 }}>
                          <FormikTextInput
                            name={`pct_${idx}`}
                            label=""
                            inputRestriction="numeric"
                            size="small"
                            min={0}
                            max={100 - allocations.reduce((acc, c, i) => (i === idx ? acc : acc + (Number(c.percentage) || 0)), 0)}
                            maxLength={3}
                            error={!!fieldErrors[`pct_${idx}`]}
                            helperText={fieldErrors[`pct_${idx}`] || ""}
                            onChange={(e) => { handleChange(idx, "percentage", e.target.value); setFieldErrors((prev) => { const n = {...prev}; delete n[`pct_${idx}`]; return n; }); }}
                            InputProps={{ endAdornment: <InputAdornment position="end" sx={{ "& .MuiTypography-root": { fontSize: "12px", color: "#64748B", fontWeight: 600 } }}>%</InputAdornment> }}
                            sx={{ "& .MuiInputBase-root": { backgroundColor: "#fff", borderRadius: "6px" }, "& .MuiInputBase-input": { py: "6px", fontWeight: 600, color: color }, "& .MuiFormHelperText-root": { fontSize: "9px", mt: 0.3, color: "#EF4444" } }}
                          />
                        </Box>
                        {allocations.length > 1 && (
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveProject(idx)}
                            sx={{ p: 0.5, color: "#EF4444", backgroundColor: "#FEF2F2", "&:hover": { backgroundColor: "#FEE2E2", color: "#DC2626" } }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        )}
                      </Box>
                    )})}
                  </Form>
                )}
              </Formik>

              {totalPercentage < 100 && (
                <Button
                  onClick={handleAddProject}
                  variant="outlined"
                  size="small"
                  startIcon={<AddCircleOutlineIcon />}
                  sx={{
                    textTransform: "none",
                    borderStyle: "dashed",
                    borderColor: "#CBD5E1",
                    color: "#64748B",
                    borderRadius: "10px",
                    py: 1,
                    mb: 1,
                    "&:hover": { borderColor: "#305533", color: "#305533", backgroundColor: "#F2F7F4" },
                  }}
                >
                  Add Project
                </Button>
              )}
                </Box>
              </>
            )}

            <Box sx={{ display: "flex", gap: 1, mt: 1.5, pt: 1, borderTop: "1px solid #E2E8F0", justifyContent: "flex-end" }}>
              <Button onClick={handleCancelClick} size="small" sx={{ color: "#64748B", textTransform: "none", fontWeight: 600 }}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveClick}
                variant="contained"
                size="small"
                disabled={!isLeave && (totalPercentage !== 100 || duplicateIndexes.size > 0)}
                startIcon={<SaveOutlinedIcon />}
                sx={{
                  backgroundColor: "#305533",
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 600,
                  px: 2.5,
                  borderRadius: "8px",
                  "&:hover": { backgroundColor: "#1e3a21" },
                  "&.Mui-disabled": { backgroundColor: "#CBD5E1", color: "#F1F5F9" },
                }}
              >
                {isExisting ? "Update" : "Save"}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            {/* View Mode */}
            {isLeave ? (
              <Box sx={{ mb: 1.5, flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", py: 4 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#64748B" }}>
                  This employee is on long leave.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569" }}>
                    Allocated Projects
                  </Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "#16A34A" }}>
                  {totalPercentage}%
                </Typography>
              </Box>
              
              <Box sx={{ display: "flex", height: 10, borderRadius: "6px", overflow: "hidden", mb: 1.5, backgroundColor: "#E2E8F0" }}>
                {validAllocations.map((a, i) => (
                  <Tooltip title={`${a.project} (${a.percentage}%)`} placement="top" arrow key={i}>
                    <Box
                      sx={{
                        width: `${a.percentage}%`,
                        backgroundColor: PROJECT_COLORS[i % PROJECT_COLORS.length],
                        transition: "all 0.4s ease",
                        borderRight: i < validAllocations.length - 1 ? "2px solid #fff" : "none",
                        "&:hover": { filter: "brightness(1.1)", cursor: "pointer" }
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>

              <Box sx={{ maxHeight: "120px", overflowY: "auto", pr: 0.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
                {validAllocations.length === 0 ? (
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 3, gap: 1, backgroundColor: "#F8FAFC", borderRadius: "8px", border: "1px dashed #CBD5E1" }}>
                    <AccessTimeOutlinedIcon sx={{ fontSize: 24, color: "#94A3B8" }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#64748B" }}>
                      No allocations yet
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#94A3B8" }}>
                      Click 'Edit Details' to assign
                    </Typography>
                  </Box>
                ) : (
                  validAllocations.map((a, i) => (
                    <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, borderRadius: "8px", backgroundColor: "#F8FAFC", border: "1px solid", borderColor: "rgba(0,0,0,0.04)", transition: "all 0.2s ease", "&:hover": { backgroundColor: "#F1F5F9", transform: "translateX(2px)" } }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: PROJECT_COLORS[i % PROJECT_COLORS.length],
                          flexShrink: 0,
                          boxShadow: `0 0 0 2px ${PROJECT_COLORS[i % PROJECT_COLORS.length]}33`,
                        }}
                      />
                      <Tooltip title={a.project} arrow disableHoverListener={a.project.length < 20}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155", flex: 1 }} noWrap>
                          {a.project}
                        </Typography>
                      </Tooltip>
                      <Typography variant="caption" sx={{ fontWeight: 700, px: 1, py: 0.25, borderRadius: "4px", backgroundColor: `${PROJECT_COLORS[i % PROJECT_COLORS.length]}1A`, color: PROJECT_COLORS[i % PROJECT_COLORS.length] }}>
                        {a.percentage}%
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
            )}

            <Box sx={{ mt: "auto", display: "flex", justifyContent: "flex-end" }}>
              {!isSubmitted && (
                <Button
                  onClick={() => {
                    setIsEditing(true);
                    if (onEdit) onEdit();
                  }}
                  variant="outlined"
                  size="small"
                  startIcon={<EditOutlinedIcon />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "8px",
                    borderColor: "#CBD5E1",
                    color: "#475569",
                    "&:hover": { borderColor: "#94A3B8", backgroundColor: "#F8FAFC" },
                  }}
                >
                  Edit Details
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Card>
  );
};

// ─── Overall Submit Overlay ───────────────────────────────────────────────────
const SubmitOverlay = ({ open, savedCount, totalCount, isSubmitting, onCancel, onConfirm }) => {
  if (!open) return null;
  return (
    <Box
      sx={{
        
  position: "absolute",
        inset: 0,
        zIndex: 1300,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(6px)",
        bgcolor: "rgba(255,255,255,0.4)",
        borderRadius: "16px",
      }}

    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
          p: 4,
          borderRadius: 3,
          textAlign: "center",
          bgcolor: "#fff",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
        }}
      >
        <CheckCircleOutlineIcon sx={{ fontSize: 52, color: "success.main", mb: 1.5 }} />
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
          Confirm Submission
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
          You are about to submit allocations for <b>{savedCount}</b> of <b>{totalCount}</b> employees.
          This action will finalize their project assignments.
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={onCancel}
            disabled={isSubmitting}
            sx={{ textTransform: "none", borderRadius: "8px", px: 3, borderColor: "#CBD5E1", color: "#64748B" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={onConfirm}
            disabled={isSubmitting}
            sx={{ textTransform: "none", borderRadius: "8px", px: 3, backgroundColor: "#305533", "&:hover": { backgroundColor: "#1e3a21" } }}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const ResourceAllocation = () => {
  const { data: empData, isLoading } = useGetEmployeeDataQuery();
  const { data: projectData } = useGetProjectDataQuery();
  const employeeList = empData?.data || [];
  const projectList = projectData?.data || [];
  const [employeeStatuses, setEmployeeStatuses] = useState({});
  const [savedAllocations, setSavedAllocations] = useState({});
  const [submitModal, setSubmitModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addAllocation] = useAddAllocationMutation();
  const [updateAllocation] = useUpdateAllocationMutation();
  const [overallSubmit] = useRmOverallSubmitMutation();

  // Profile image fetching
  const [fetchImage] = useLazyGetImageQuery();
  const [imageMap, setImageMap] = useState({});
  const cachedImageMap = useRef({});

  useEffect(() => {
    const fetchImages = async () => {
      if (!employeeList?.length) return;
      const updatedMap = { ...cachedImageMap.current };
      for (const emp of employeeList) {
        if (updatedMap[emp.empId]) continue;
        try {
          if (emp.profilePicture?.fileName && emp.profilePicture?.folderName) {
            updatedMap[emp.empId] = await fetchImage({
              fileName: emp.profilePicture.fileName,
              folderName: emp.profilePicture.folderName,
            }).unwrap();
          }
        } catch (e) {
          console.error(`Image fetch failed for ${emp.empId}`, e);
        }
      }
      cachedImageMap.current = updatedMap;
      setImageMap(updatedMap);
    };
    if (employeeList.length > 0) fetchImages();
  }, [employeeList]);

  // Build lookup maps from project data
  const projectMap = useMemo(() => {
    const map = {};
    projectList.forEach((p) => {
      map[p.empId] = p;
    });
    return map;
  }, [projectList]);

  // Initialize statuses from project data
  useEffect(() => {
    if (projectList.length > 0) {
      const statuses = {};
      projectList.forEach((p) => {
        if (p.action?.toLowerCase() === "submit" || p.rmStatus?.toLowerCase() === "submitted") {
          statuses[p.empId] = "submitted";
        } else if (p.rmStatus?.toLowerCase() === "pending" || p.action?.toLowerCase() === "save") {
          statuses[p.empId] = "saved";
        } else {
          statuses[p.empId] = "pending";
        }
      });
      setEmployeeStatuses((prev) => ({ ...prev, ...statuses }));
    }
  }, [projectList]);

  const getStatus = (empId) => employeeStatuses[empId] || "pending";

  const handleSave = async (empId, allocations, isLeave) => {
    try {
      const payload = {
        empId,
        action: "Save",
        ...(isLeave ? { leaveStatus: "yes" } : { leaveStatus: "no" }),
        projectDetails: isLeave ? [] : allocations
          .filter((a) => a.project && a.percentage > 0)
          .map((a) => ({ projectName: a.project, projectPercentage: a.percentage })),
      };
      const existing = projectMap[empId];
      if (existing?.id) {
        await updateAllocation({ id: existing.id, ...payload }).unwrap();
         toast("success","Allocation updated successfully");
      } else {
        await addAllocation(payload).unwrap();
        toast("success","Allocation saved successfully");

      }
      setSavedAllocations((prev) => ({ ...prev, [empId]: allocations }));
      setEmployeeStatuses((prev) => ({ ...prev, [empId]: "saved" }));
    } catch (e) {
      console.error("Save failed for", empId, e);
      toast("error","Failed to save allocation");
    }
  };

  const handleEdit = (empId) => {
    setEmployeeStatuses((prev) => ({ ...prev, [empId]: "pending" }));
  };

  const savedCount = Object.values(employeeStatuses).filter((s) => s === "saved").length;
  const submittedCount = Object.values(employeeStatuses).filter((s) => s === "submitted").length;
  const totalCount = employeeList.length;
  const allSaved = totalCount > 0 && savedCount > 0 && (savedCount + submittedCount) === totalCount;
  const allAreSaved = totalCount > 0 && savedCount === totalCount;

  const handleSubmitConfirm = async () => {
    setIsSubmitting(true);
    try {
      await overallSubmit("submit").unwrap();
      const updated = {};
      Object.keys(employeeStatuses).forEach((id) => {
        updated[id] = employeeStatuses[id] === "saved" ? "submitted" : employeeStatuses[id];
      });
      setEmployeeStatuses(updated);
      setSubmitModal(false);
    } catch (e) {
      console.error("Submit failed", e);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 2,
        p: 2.5,
        borderRadius: "16px",
        backgroundColor: "#ffffff",
        // boxShadow: "0px 4px 20px rgba(0,0,0,0.06)",
        height: "88vh",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header Row */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1E293B" }}>
            Resource Allocation
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select projects and assign allocation percentages for each employee. Save changes before submitting.
          </Typography>
        </Box>
        {submittedCount < totalCount && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {!isLoading && totalCount > 0 && (
            <Chip
              label={`${savedCount + submittedCount} / ${totalCount} Saved`}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: "12px",
                backgroundColor: allSaved ? "#DCFCE7" : "#FEF3C7",
                color: allSaved ? "#16A34A" : "#D97706",
              }}
            />
          )}
          <Tooltip title={!allAreSaved ? "All employees must be saved before submitting" : "Submit all saved allocations"} arrow>
            <span>
              <StyledButton
                label="Overall Submit"
                variant="contained"
                onClick={() => setSubmitModal(true)}
                disabled={!allAreSaved}
                sx={{
                  backgroundColor: allAreSaved ? "#305533" : undefined,
                  color: "#fff",
                  fontSize: "13px",
                  borderRadius: "8px",
                  "&:hover": { backgroundColor: "#1e3a21" },
                }}
              />
            </span>
          </Tooltip>
        </Box>
        )}
      </Box>

      {/* Employee Grid */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          pr: 0.5,
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "#CBD5E1", borderRadius: "3px" },
        }}
      >
        {isLoading ? (
          <Typography>Loading...</Typography>
        ) : employeeList.length > 0 ? (
          <Grid container spacing={2.5}>
            {employeeList.map((emp) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                key={emp.empId || Math.random()}
              >
                <EmployeeCard
                  employee={emp}
                  profileUrl={imageMap[emp.empId] || null}
                  status={getStatus(emp.empId)}
                  onSave={(allocs, isLeave) => handleSave(emp.empId, allocs, isLeave)}
                  onEdit={() => handleEdit(emp.empId)}
                  isExisting={!!projectMap[emp.empId]?.id}
                  isLeaveInitial={projectMap[emp.empId]?.leaveStatus === "yes"}
                  initialAllocations={
                    projectMap[emp.empId]?.projectDetails?.map((p) => ({
                      project: p.projectName,
                      percentage: Number(p.projectPercentage) || 0,
                    })) || []
                  }
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body1" sx={{ textAlign: "center", mt: 6, color: "text.secondary" }}>
            No team members found.
          </Typography>
        )}
      </Box>

      {/* Confirm Submit Overlay */}
      <SubmitOverlay
        open={submitModal}
        savedCount={savedCount}
        totalCount={totalCount}
        isSubmitting={isSubmitting}
        onCancel={() => !isSubmitting && setSubmitModal(false)}
        onConfirm={handleSubmitConfirm}
      />
    </Box>
  );
};

export default ResourceAllocation;
